import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from 'common';
import { ref as databaseRef, onValue, update } from 'firebase/database';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Link, Paper, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';

const FUNCTION_BASE = 'https://europe-west1-allodriver-9139a.cloudfunctions.net';

const STATUS_COLORS = {
  NEW: 'default',
  DOCS_PENDING: 'warning',
  READY_FOR_REVIEW: 'info',
  APPROVED: 'success',
  REJECTED: 'error'
};

const STATUS_LABELS = {
  NEW: 'Nouvelle',
  DOCS_PENDING: 'Documents en attente',
  READY_FOR_REVIEW: 'À valider',
  APPROVED: 'Approuvée',
  REJECTED: 'Refusée'
};

const DOC_LABELS = {
  vtcCard: 'Carte VTC (recto)',
  vtcCardBack: 'Carte VTC (verso)',
  kbis: 'Kbis',
  vehicleInsurance: 'Assurance véhicule',
  rcPro: 'RC Pro',
  vehicleRegistration: 'Carte grise (recto)',
  vehicleRegistrationBack: 'Carte grise (verso)',
  licenseImage: 'Permis de conduire'
};

export default function DriverRequests() {
  const { auth, database } = useContext(FirebaseContext);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });
  const [docsDialog, setDocsDialog] = useState({ open: false, req: null });

  useEffect(() => {
    if (!database) return;
    const r = databaseRef(database, 'driverAccessRequests');
    const unsub = onValue(r, snap => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setRequests(list);
      setLoading(false);
    });
    return () => unsub();
  }, [database]);

  const getToken = async () => {
    if (!auth || !auth.currentUser) throw new Error('Non connecté');
    return auth.currentUser.getIdToken(true);
  };

  const handleApprove = async (req) => {
    setApproving(req.id);
    try {
      const token = await getToken();
      const res = await fetch(`${FUNCTION_BASE}/approve_driver_request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ requestId: req.id })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.message || json.error || 'Erreur serveur');
      setToast({ open: true, msg: `Compte chauffeur créé (uid ${String(json.uid).slice(0, 8)}…).`, severity: 'success' });
    } catch (e) {
      setToast({ open: true, msg: `Erreur : ${e.message}`, severity: 'error' });
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async () => {
    const { id, reason } = rejectDialog;
    setRejectDialog(d => ({ ...d, open: false }));
    try {
      await update(databaseRef(database, `driverAccessRequests/${id}`), {
        status: 'REJECTED',
        rejectedAt: Date.now(),
        rejectionReason: reason || ''
      });
      setToast({ open: true, msg: 'Demande refusée.', severity: 'info' });
    } catch (e) {
      setToast({ open: true, msg: `Erreur : ${e.message}`, severity: 'error' });
    }
  };

  const fmt = (ts) => ts ? new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const docCount = (req) => Object.keys(req.documents || {}).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <LocalTaxiIcon sx={{ color: '#2563eb', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={800}>Demandes chauffeurs</Typography>
        <Chip
          label={`${requests.filter(r => r.status === 'READY_FOR_REVIEW').length} à valider`}
          color="info"
          size="small"
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : requests.length === 0 ? (
        <Alert severity="info">Aucune demande pour l'instant.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f5f5' }}>
                <TableCell><strong>Chauffeur</strong></TableCell>
                <TableCell><strong>Tél.</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Ville</strong></TableCell>
                <TableCell><strong>Véhicule</strong></TableCell>
                <TableCell><strong>Docs</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map(req => (
                <TableRow
                  key={req.id}
                  sx={{ '&:hover': { background: '#fafafa' }, opacity: ['APPROVED', 'REJECTED'].includes(req.status) ? 0.7 : 1 }}
                >
                  <TableCell>
                    <Typography fontWeight={700} fontSize={13}>{`${req.firstName || ''} ${req.lastName || ''}`.trim() || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.mobile}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.email || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.city || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{[req.vehicleMake, req.vehicleModel].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<DescriptionOutlinedIcon fontSize="small" />}
                      onClick={() => setDocsDialog({ open: true, req })}
                      disabled={docCount(req) === 0}
                    >
                      {docCount(req)}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(req.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[req.status] || req.status}
                      color={STATUS_COLORS[req.status] || 'default'}
                      size="small"
                    />
                    {req.linkedUid && (
                      <Typography fontSize={10} color="text.secondary" mt={0.5} sx={{ fontFamily: 'monospace' }}>
                        uid: {req.linkedUid.slice(0, 8)}…
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {req.status === 'READY_FOR_REVIEW' && (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Approuver — créer le compte chauffeur">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={approving === req.id}
                              onClick={() => handleApprove(req)}
                            >
                              {approving === req.id
                                ? <CircularProgress size={18} />
                                : <CheckCircleOutlineIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Refuser">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRejectDialog({ open: true, id: req.id, reason: '' })}
                          >
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Documents dialog */}
      <Dialog open={docsDialog.open} onClose={() => setDocsDialog({ open: false, req: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Documents — {docsDialog.req ? `${docsDialog.req.firstName || ''} ${docsDialog.req.lastName || ''}`.trim() : ''}</DialogTitle>
        <DialogContent>
          {docsDialog.req && Object.keys(docsDialog.req.documents || {}).length === 0 ? (
            <Alert severity="info">Aucun document fourni.</Alert>
          ) : (
            <Stack spacing={1} mt={1}>
              {docsDialog.req && Object.entries(docsDialog.req.documents || {}).map(([type, entry]) => (
                <Stack key={type} direction="row" alignItems="center" justifyContent="space-between" sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
                  <Typography fontSize={13} fontWeight={600}>{DOC_LABELS[type] || type}</Typography>
                  {entry && entry.url ? (
                    <Link href={entry.url} target="_blank" rel="noopener" fontSize={12}>Ouvrir</Link>
                  ) : (
                    <Typography fontSize={12} color="text.secondary">—</Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsDialog({ open: false, req: null })}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Refuser la demande</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Raison du refus (optionnel)"
            multiline
            minRows={2}
            value={rejectDialog.reason}
            onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(d => ({ ...d, open: false }))}>Annuler</Button>
          <Button onClick={handleReject} color="error" variant="contained">Refuser</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ width: '100%' }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
